import React, { useEffect, useRef, useState } from "react";
import { useAccessActions, useAccessData, useAccessRefreshState } from "../auth/AccessProvider";
import "./AccessRefreshNotice.css";

const MANUAL_RETRY_COOLDOWN_MS = 30000;

export function AccessRefreshNoticePanel({ refreshError, refreshing, retryAt = 0, refreshAccessContext }) {
  const [pending, setPending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [clock, setClock] = useState(0);
  const inFlight = useRef(false);
  const active = useRef(true);
  const previousFailure = useRef(false);
  const rateLimited = String(refreshError?.code || "").replace(/^functions\//, "") === "resource-exhausted";
  const retryAvailableAt = Math.max(cooldownUntil, rateLimited ? retryAt : 0);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    if (refreshError) previousFailure.current = true;
    else if (!refreshing && !pending) previousFailure.current = false;
  }, [refreshError, refreshing, pending]);

  // Update the button once its cooldown ends; this timer makes no requests.
  useEffect(() => {
    const delay = retryAvailableAt - Date.now();
    if (delay <= 0) return undefined;
    const timer = setTimeout(() => setClock((value) => value + 1), Math.min(2147483647, delay));
    return () => clearTimeout(timer);
  }, [retryAvailableAt, clock]);

  if (!refreshError && !pending && !(refreshing && previousFailure.current)) return null;

  const busy = pending || refreshing;
  const retry = async () => {
    if (inFlight.current || refreshing || Date.now() < retryAvailableAt) return;
    inFlight.current = true;
    setPending(true);
    setCooldownUntil(Date.now() + MANUAL_RETRY_COOLDOWN_MS);
    try {
      await refreshAccessContext({ background: true, forceTokenRefresh: true });
    } catch {
      // AccessProvider retains temporary failures or clears denied access.
    } finally {
      inFlight.current = false;
      if (active.current) setPending(false);
    }
  };

  return (
    <aside className="pa-access-refresh-notice" role="status" aria-label="Access refresh status" aria-live="polite" aria-atomic="true">
      <span className="pa-access-refresh-notice__icon" aria-hidden="true">!</span>
      <div className="pa-access-refresh-notice__copy">
        <strong>{busy ? "Checking your access…" : "Access check delayed"}</strong>
        <p>{busy
          ? "You can keep working while we check your access."
          : "We couldn't refresh your access. Your current view may be out of date. We'll retry automatically."}</p>
      </div>
      <button type="button" disabled={busy || retryAvailableAt > Date.now()} onClick={retry}>
        {busy ? "Checking…" : retryAvailableAt > Date.now() ? "Retry shortly" : "Retry access check"}
      </button>
    </aside>
  );
}

export default function AccessRefreshNotice() {
  const { user, loading, accessContext, activeTenant } = useAccessData();
  const { refreshError, refreshing, retryAt } = useAccessRefreshState();
  const { refreshAccessContext } = useAccessActions();
  if (!user || loading || !accessContext) return null;
  return (
    <AccessRefreshNoticePanel
      key={JSON.stringify([user.uid, user.emailVerified, activeTenant?.tenantId])}
      refreshError={refreshError}
      refreshing={refreshing}
      retryAt={retryAt}
      refreshAccessContext={refreshAccessContext}
    />
  );
}
